import React, { useEffect, useCallback, useState } from "react";
import { columnsRequestPayments, } from "../../Global Functions/columnsAntd";
import { useDispatch, useSelector } from "react-redux";
import { listRequestPayments, updateStatus } from "../../Redux/Actions/rentalBookingActions";
import toast from "react-hot-toast";
import TableComponentsCp from "../../Global Functions/tableComponentsCp";
import DateRangePopup from "../../Utils/dateFilterPopup";
import { Modal, Input, InputNumber, Checkbox } from "antd";
import axios from "axios"; // added — same direct-call pattern as ListConfig.jsx

const ListRequestPaymentsRefund = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL; // added

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
  });

  const { admin, payment_executive } = useSelector((state) => state.admin);
  const loggedUser = admin?.data ? admin : payment_executive;
  const role = loggedUser?.data?.role;

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [rejectPopupVisible, setRejectPopupVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [loadingReject, setLoadingReject] = useState(false);
  const [approvePopupVisible, setApprovePopupVisible] = useState(false);
  const [selectedApprovalRecord, setSelectedApprovalRecord] = useState(null);
  const [loadingApprove, setLoadingApprove] = useState(false);
  const [viewReasonVisible, setViewReasonVisible] = useState(false);
  const [viewReasonRecord, setViewReasonRecord] = useState(null);
  const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [includeGatewayCharges, setIncludeGatewayCharges] = useState(false);
  const [includeConvenienceFee, setIncludeConvenienceFee] = useState(false);

  // Direct-fetched global settings (Payment Gateway Charges / Rental Convenience Fees) — added
  const [globalSettings, setGlobalSettings] = useState(null);

  // New states for refund type
  const [refundType, setRefundType] = useState("full");
  const [deductionPercentage, setDeductionPercentage] = useState(null);
  const [percentageError, setPercentageError] = useState("");

  const dispatch = useDispatch();

  const fetchDetails = useCallback(
    (page, pageSize, showLoader = true) => {
      const payload = {
        page,
        limit: pageSize,
        is_deleted: false,
        business_type: "rental",
        filter: debouncedSearch,
        ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
        ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
      };
      dispatch(listRequestPayments(payload, showLoader)).catch(() => {
        if (!showLoader) {
          toast.error("Failed to refresh payment history list");
        }
      });
    },
    [dispatch, debouncedSearch, dateRange]
  );

  useEffect(() => {
    fetchDetails(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, fetchDetails]);

  // Direct fetch of settings, same endpoint/shape as ListConfig.jsx's getConfigData
  useEffect(() => {
    const getSettings = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/setting/view`, {
          headers: { "Content-Type": "application/json" },
        });
        if (response.data?.data) {
          setGlobalSettings(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    getSettings();
  }, [BASE_URL]);

  const { paymentsRequest } = useSelector((state) => state.rentalBooking || {});
  const loading = useSelector((state) => state.payments?.loading);

  useEffect(() => {
    if (paymentsRequest?.total) {
      setPagination((prevData) => ({ ...prevData, total: paymentsRequest.total }));
    }
  }, [paymentsRequest]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPagination(prev => ({ ...prev, current: 1 }));
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const handleDateRangeApply = (start, end) => {
    if (start === null && end === null) {
      setDateRange({ start: null, end: null });
    } else {
      setDateRange({ start, end });
    }
    setIsDatePopupVisible(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const openViewReasonPopup = (record) => {
    setViewReasonRecord(record);
    setViewReasonVisible(true);
  };

  const handleStatusChange = async (
    record,
    action,
    refundReason = "",
    refund_status = "full",
    totalAmount = null,
    deductionPercentage = null,
    gatewayCharges = null,
    convenienceFee = null
  ) => {
    const payload = {
      rental_booking_id: record.rental_booking_id,
      status: action,
      refund_reason_from_admin: refundReason,
      refund_status,
      ...(totalAmount !== null && { amount: totalAmount }),
      ...(refund_status === "partial" && deductionPercentage !== null && { deduction_percentage: deductionPercentage }),
      ...(gatewayCharges !== null && { payment_gateway_charges: gatewayCharges }),
      ...(convenienceFee !== null && { convenience_fee: convenienceFee }),
    };

    await dispatch(updateStatus(record._id, payload));
    fetchDetails(pagination.current, pagination.pageSize);
  };

  // Open approval popup
  const openApprovePopup = (record) => {
    setSelectedApprovalRecord(record);
    setRefundType("full");
    setDeductionPercentage(null);
    setPercentageError("");
    setIncludeGatewayCharges(false);
    setIncludeConvenienceFee(false);
    setApprovePopupVisible(false);
    // slight delay to reset state before showing
    setTimeout(() => setApprovePopupVisible(true), 0);
  };

  // Fallback chain: record's own charge if non-zero, else the global settings default.
  // Key names guessed from ListConfig.jsx's formatLabel() reversing "Payment Gateway Charges"
  // and "Rental Convenience Fees" — VERIFY against the real /setting/view response and fix
  // the two keys below if they don't match (e.g. could be rental_convenience_fee, singular).
  const gatewayChargeValue =
    selectedApprovalRecord?.rental_booking?.payment_gateway_amount > 0
      ? selectedApprovalRecord.rental_booking.payment_gateway_amount
      : Number(globalSettings?.payment_gateway_charges) || 0;

  const convenienceFeeValue =
    selectedApprovalRecord?.rental_booking?.convenience_fee > 0
      ? selectedApprovalRecord.rental_booking.convenience_fee
      : Number(globalSettings?.rental_convenience_fees) || 0;

  const totalRefundAmount =
    (selectedApprovalRecord?.refund_amount || 0) +
    (includeGatewayCharges ? gatewayChargeValue : 0) +
    (includeConvenienceFee ? convenienceFeeValue : 0);

  // Submit approval
  const submitApprove = async () => {
    if (refundType === "partial") {
      if (deductionPercentage === null || deductionPercentage === undefined || deductionPercentage === "") {
        setPercentageError("Please enter a deduction percentage.");
        return;
      }
      if (deductionPercentage < 0 || deductionPercentage > 100) {
        setPercentageError("Percentage must be between 0 and 100.");
        return;
      }
    }

    setLoadingApprove(true);
    try {
      await handleStatusChange(
        selectedApprovalRecord,
        "approved",
        "",
        refundType,
        totalRefundAmount,                                   // ← real total (base + selected charges)
        refundType === "partial" ? deductionPercentage : null,
        includeGatewayCharges ? gatewayChargeValue : null,
        includeConvenienceFee ? convenienceFeeValue : null
      );
      toast.success("Payment request approved successfully.");
      setApprovePopupVisible(false);
    } catch (error) {
      toast.error("Failed to approve payment request.");
    }
    setLoadingApprove(false);
  };

  // Open rejection popup
  const openRejectPopup = (record) => {
    setSelectedRecord(record);
    setRejectReason("");
    setRejectPopupVisible(true);
  };

  // Submit rejection with reason
  const submitReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please enter a rejection reason.");
      return;
    }
    setLoadingReject(true);
    try {
      await handleStatusChange(selectedRecord, "rejected", rejectReason.trim());
      toast.success("Payment request rejected successfully.");
      setRejectPopupVisible(false);
    } catch (error) {
      toast.error("Failed to reject payment request.");
    }
    setLoadingReject(false);
  };

  return (
    <>
      <TableComponentsCp
        dataSource={Array.isArray(paymentsRequest?.data) ? paymentsRequest.data : []}
        pagination={pagination}
        onChange={handleTableChange}
        columns={columnsRequestPayments(pagination.current, pagination.pageSize, handleStatusChange, openRejectPopup, openApprovePopup, openViewReasonPopup, role)}
        h3heading="Request Payment List"
        loading={loading}
        setState={setSearchQuery}
        onCalendarClick={() => setIsDatePopupVisible(true)}
      />

      {/* Rejection Modal */}
      <Modal
        title="Enter Rejection Reason"
        visible={rejectPopupVisible}
        onOk={submitReject}
        onCancel={() => setRejectPopupVisible(false)}
        okButtonProps={{
          disabled: loadingReject,
          style: { backgroundColor: "#0D152B", color: "white", border: "none" },
        }}
        cancelButtonProps={{ disabled: loadingReject }}
        confirmLoading={loadingReject}
      >
        <Input.TextArea
          rows={4}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Write rejection reason here..."
          disabled={loadingReject}
        />
      </Modal>

      {/* Approval Modal */}
      <Modal
        title={null}
        visible={approvePopupVisible}
        onOk={submitApprove}
        onCancel={() => {
          setApprovePopupVisible(false);
          setRefundType("full");
          setDeductionPercentage(null);
          setPercentageError("");
          setIncludeGatewayCharges(false);
          setIncludeConvenienceFee(false);
        }}
        okText="Confirm Refund"
        cancelText="Cancel"
        okButtonProps={{
          disabled: loadingApprove,
          style: { backgroundColor: "#0D152B", color: "white", border: "none", borderRadius: "8px", height: "38px", fontWeight: 500 },
        }}
        cancelButtonProps={{
          disabled: loadingApprove,
          style: { borderRadius: "8px", height: "38px" }
        }}
        confirmLoading={loadingApprove}
        width={460}
        bodyStyle={{ padding: "0" }}
      >
        {/* Header */}
        <div style={{
          background: "#0D152B",
          borderRadius: "8px 8px 0 0",
          padding: "20px 24px",
          display: "flex",
          alignItems: "center",
          gap: "12px"
        }}>
          <div style={{
            width: "36px", height: "36px", borderRadius: "50%",
            background: "rgba(255,255,255,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <span style={{ color: "white", fontSize: "18px" }}>↩</span>
          </div>
          <div>
            <p style={{ color: "white", fontWeight: 600, fontSize: "15px", margin: 0 }}>Approve Refund</p>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "12px", margin: 0 }}>
              Booking ID: {selectedApprovalRecord?.rental_booking?.booking_id || "—"}
            </p>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "24px" }}>

          {/* Amount display */}
          <div style={{
            background: "#f5f7fa",
            border: "1px solid #e8e8e8",
            borderRadius: "10px",
            padding: "14px 18px",
            marginBottom: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <span style={{ fontSize: "13px", color: "#888" }}>Requested Amount</span>
            <span style={{ fontSize: "18px", fontWeight: 600, color: "#0D152B" }}>
              {selectedApprovalRecord?.booking?.currency === "INR" ? "₹" : selectedApprovalRecord?.booking?.currency || "₹"}
              {selectedApprovalRecord?.refund_amount || "—"}
            </span>
          </div>

          {/* Optional charges checkboxes — only show if a value is actually available */}
          {(gatewayChargeValue > 0 || convenienceFeeValue > 0) && (
            <div style={{
              background: "#fafafa",
              border: "1px solid #e8e8e8",
              borderRadius: "10px",
              padding: "14px 16px",
              marginBottom: "16px"
            }}>
              <p style={{ fontSize: "13px", color: "#888", marginBottom: "10px", fontWeight: 500 }}>
                INCLUDE ADDITIONAL CHARGES
              </p>

              {gatewayChargeValue > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <Checkbox
                    checked={includeGatewayCharges}
                    onChange={(e) => setIncludeGatewayCharges(e.target.checked)}
                    disabled={loadingApprove}
                  >
                    Payment Gateway Charges
                  </Checkbox>
                  <span style={{ fontSize: "14px", fontWeight: 500, color: "#0D152B" }}>₹{gatewayChargeValue}</span>
                </div>
              )}

              {convenienceFeeValue > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Checkbox
                    checked={includeConvenienceFee}
                    onChange={(e) => setIncludeConvenienceFee(e.target.checked)}
                    disabled={loadingApprove}
                  >
                    Convenience Fee
                  </Checkbox>
                  <span style={{ fontSize: "14px", fontWeight: 500, color: "#0D152B" }}>₹{convenienceFeeValue}</span>
                </div>
              )}
            </div>
          )}

          {/* Live total — updates as checkboxes toggle */}
          {(includeGatewayCharges || includeConvenienceFee) && (
            <div style={{
              background: "#f0f2f8",
              border: "1px solid #0D152B",
              borderRadius: "10px",
              padding: "12px 18px",
              marginBottom: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#0D152B" }}>Total Refund Amount</span>
              <span style={{ fontSize: "18px", fontWeight: 700, color: "#0D152B" }}>₹{totalRefundAmount}</span>
            </div>
          )}

          {/* Refund type label */}
          <p style={{ fontSize: "13px", color: "#888", marginBottom: "10px", fontWeight: 500 }}>
            SELECT REFUND TYPE
          </p>

          {/* Radio cards */}
          <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
            {/* Full Refund Card */}
            <div
              onClick={() => { if (!loadingApprove) { setRefundType("full"); setDeductionPercentage(null); setPercentageError(""); } }}
              style={{
                flex: 1,
                border: refundType === "full" ? "2px solid #0D152B" : "1.5px solid #e0e0e0",
                borderRadius: "10px",
                padding: "14px 16px",
                cursor: loadingApprove ? "not-allowed" : "pointer",
                background: refundType === "full" ? "#f0f2f8" : "white",
                transition: "all 0.2s",
                position: "relative"
              }}
            >
              {refundType === "full" && (
                <div style={{
                  position: "absolute", top: "8px", right: "8px",
                  width: "18px", height: "18px", borderRadius: "50%",
                  background: "#0D152B", display: "flex", alignItems: "center", justifyContent: "center"
                }}>
                  <span style={{ color: "white", fontSize: "11px", lineHeight: 1 }}>✓</span>
                </div>
              )}
              <p style={{ fontWeight: 600, fontSize: "14px", margin: "0 0 4px", color: "#0D152B" }}>Full Refund</p>
              <p style={{ fontSize: "12px", color: "#888", margin: 0 }}>100% returned to customer</p>
            </div>

            {/* Partial Refund Card */}
            <div
              onClick={() => { if (!loadingApprove) setRefundType("partial"); }}
              style={{
                flex: 1,
                border: refundType === "partial" ? "2px solid #0D152B" : "1.5px solid #e0e0e0",
                borderRadius: "10px",
                padding: "14px 16px",
                cursor: loadingApprove ? "not-allowed" : "pointer",
                background: refundType === "partial" ? "#f0f2f8" : "white",
                transition: "all 0.2s",
                position: "relative"
              }}
            >
              {refundType === "partial" && (
                <div style={{
                  position: "absolute", top: "8px", right: "8px",
                  width: "18px", height: "18px", borderRadius: "50%",
                  background: "#0D152B", display: "flex", alignItems: "center", justifyContent: "center"
                }}>
                  <span style={{ color: "white", fontSize: "11px", lineHeight: 1 }}>✓</span>
                </div>
              )}
              <p style={{ fontWeight: 600, fontSize: "14px", margin: "0 0 4px", color: "#0D152B" }}>Partial Refund</p>
              <p style={{ fontSize: "12px", color: "#888", margin: 0 }}>Deduct a percentage</p>
            </div>
          </div>

          {/* Partial input section */}
          {refundType === "partial" && (
            <div style={{
              background: "#fafafa",
              border: "1px solid #e8e8e8",
              borderRadius: "10px",
              padding: "16px",
              marginBottom: "4px"
            }}>
              <label style={{ fontSize: "13px", fontWeight: 500, color: "#555", display: "block", marginBottom: "8px" }}>
                Deduction Percentage <span style={{ color: "red" }}>*</span>
              </label>
              <InputNumber
                min={0}
                max={100}
                value={deductionPercentage}
                onChange={(value) => {
                  setDeductionPercentage(value);
                  if (value > 100) setPercentageError("Percentage cannot exceed 100.");
                  else if (value < 0) setPercentageError("Percentage cannot be less than 0.");
                  else setPercentageError("");
                }}
                placeholder="e.g. 20"
                style={{ width: "100%", borderRadius: "8px" }}
                disabled={loadingApprove}
                addonAfter="%"
              />
              {percentageError && (
                <p style={{ color: "red", fontSize: "12px", margin: "6px 0 0" }}>{percentageError}</p>
              )}

              {/* Live breakdown */}
              {deductionPercentage !== null && deductionPercentage >= 0 && deductionPercentage <= 100 && !percentageError && (
                <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
                  <div style={{
                    flex: 1, background: "#fff0f0", border: "1px solid #ffd6d6",
                    borderRadius: "8px", padding: "10px 12px", textAlign: "center"
                  }}>
                    <p style={{ fontSize: "11px", color: "#c0392b", margin: "0 0 2px" }}>Deducted</p>
                    <p style={{ fontSize: "15px", fontWeight: 600, color: "#c0392b", margin: 0 }}>
                      {deductionPercentage}%
                    </p>
                  </div>
                  <div style={{
                    flex: 1, background: "#f0fff4", border: "1px solid #b7ebc4",
                    borderRadius: "8px", padding: "10px 12px", textAlign: "center"
                  }}>
                    <p style={{ fontSize: "11px", color: "#27ae60", margin: "0 0 2px" }}>Customer gets</p>
                    <p style={{ fontSize: "15px", fontWeight: 600, color: "#27ae60", margin: 0 }}>
                      {100 - deductionPercentage}%
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        title="Rejected Refund Reason"
        visible={viewReasonVisible}
        onCancel={() => setViewReasonVisible(false)}
        footer={null}
      >
        <p>{viewReasonRecord?.refund_reason_from_admin || "No reason provided."}</p>
      </Modal>

      <DateRangePopup
        visible={isDatePopupVisible}
        initialStart={dateRange.start}
        initialEnd={dateRange.end}
        onCancel={() => setIsDatePopupVisible(false)}
        onOk={handleDateRangeApply}
      />
    </>
  );
};

export default ListRequestPaymentsRefund;